// Every client polling interval, backoff, poll timeout and attempt cap that drives network
// traffic, in one dependency-free module (no imports, no React, no Prisma, no "use client").
//
// Why one module: the load generator (Lasttest/) imports this file directly, so the
// traffic it generates always follows the cadence the site really has instead of a copy
// that drifts. All heartbeat intervals (desktop and phone) live in ./heartbeat-timing.ts
// (same reason) and are not repeated here. klient-intervall.test.ts locks every value: change one on purpose
// and the test tells you to update the generator's profiles too.
//
// All values are milliseconds unless the name says otherwise. One value used in several
// places with different meaning has one name per place.

// ── Student bar: /api/student/aktiv-lektion (LektionsBarForElev) ──────────────────────────
/** No active lesson. */
export const AKTIV_LEKTION_IDLE_MS = 60_000;
/** Active lesson, nothing that needs a tighter poll. */
export const AKTIV_LEKTION_LEKTION_MS = 15_000;
/** A game is running and the student has not joined. */
export const AKTIV_LEKTION_SPEL_MS = 5_000;
/** A genomgang is running and the student is not on the genomgang page. */
export const AKTIV_LEKTION_GENOMGANG_MS = 5_000;
/** Floor on the wait after a pathname change or a rescheduled tick, so nav rendering does not compete with the poll. */
export const AKTIV_LEKTION_NAV_PAUS_MS = 1_500;

// ── Prov: /api/prov-elev/aktivt ───────────────────────────────────────────────────────────
/** ProvVakt, no active pass yet (also when the tab is hidden). */
export const PROVVAKT_MS = 30_000;
/** ProvVakt, an active pass is running. */
export const PROVVAKT_AKTIVT_PASS_MS = 6_000;
/** useProvNavMobilparning: the nav's own light poll while on /prov-elev/. */
export const PROV_NAV_MOBILPARNING_MS = 30_000;

// ── Mobile pairing ────────────────────────────────────────────────────────────────────────
/** MobileQrButton: is the desktop still paired (current pairing)? */
export const MOBIL_PAR_CURRENT_MS = 30_000;
/** MobileQrModal: the open QR modal asks whether the phone has scanned it (setInterval). */
export const MOBIL_PAR_STATUS_MS = 2_000;
/** MobileQrModal: how long "Ansluten" shows before the modal closes and re-reads current. */
export const MOBIL_PAR_STANG_MS = 1_500;

// ── Scan result poll on the desktop (ScanLosning + resultatPoll) ──────────────────────────
/** While the task is pending (the student is still aiming the camera). */
export const SCAN_RESULTAT_VANTANDE_MS = 2_500;
/** Once the server is grading. */
export const SCAN_RESULTAT_RATTAS_MS = 400;
/** Hard deadline for the whole result poll. */
export const SCAN_RESULTAT_MAX_MS = 90_000;
/** One result request is abandoned after this. */
export const SCAN_RESULTAT_ANROP_TIMEOUT_MS = 20_000;
/** Default upper bound for the error backoff in startaPoll. */
export const RESULTAT_POLL_MAX_BACKOFF_MS = 5_000;

// ── Scan camera on the phone: /api/scan/task-status (ScanCameraClient) ────────────────────
export const SCAN_TASK_STATUS_MS = 2_000;
/** After a successful upload the phone shows "Klart" this long, then goes back to /m. */
export const SCAN_TILLBAKA_EFTER_UPPLADDNING_MS = 1_200;
export const SCAN_TASK_STATUS_MAX_MS = 360_000;
export const SCAN_TASK_STATUS_ANROP_TIMEOUT_MS = 20_000;

// ── Phone long poll: client side (MobileScanWatcher) ──────────────────────────────────────
/** The tab is hidden: wait before looking again. */
export const MOBIL_VANTAN_DOLD_FLIK_MS = 1_500;
/** After a lock-only answer: pause so two instances that disagree cannot spin. */
export const MOBIL_VANTAN_LAS_PAUS_MS = 1_000;
/** After a server error (not 200/204/401/403). */
export const MOBIL_VANTAN_SERVERFEL_BACKOFF_MS = 3_000;
/** After a network error. */
export const MOBIL_VANTAN_NATFEL_BACKOFF_MS = 2_000;

// ── Phone long poll: server side (/api/mobile-pair/wait-for-scan) ─────────────────────────
/** Väg 1 cadence: the database turn when the Runtime Cache signal cannot be trusted. */
export const MOBIL_VANTAN_DB_MS = 2_000;
/** Outside a lesson (?lugn=1): both the signal read and the database turn. */
export const MOBIL_VANTAN_LUGN_MS = 5_000;
/** Väg 2: how often the signal is read in a lesson. */
export const MOBIL_VANTAN_SIGNAL_MS = 1_000;
/** A database turn at least this often even without a signal. */
export const MOBIL_VANTAN_RESERV_MS = 15_000;
/** Length of one long poll. */
export const MOBIL_VANTAN_MAX_MS = 25_000;
/** Length of one long poll for a student in an active prov. */
export const MOBIL_VANTAN_PROV_MAX_MS = 8_000;

// ── Game state poll (useSpelState) ────────────────────────────────────────────────────────
export const SPEL_LARARE_MS = 2_000;
/** Student: base interval; the actual wait is SPEL_ELEV_MS + random * SPEL_ELEV_SPRIDNING_MS. */
export const SPEL_ELEV_MS = 2_300;
export const SPEL_ELEV_SPRIDNING_MS = 200;
/** SPEL_ELEV_MS + SPEL_ELEV_SPRIDNING_MS: the longest student wait (the generator uses it). */
export const SPEL_ELEV_MAX_MS = 2_500;
/** Student with a hidden tab. */
export const SPEL_DOLD_MS = 8_000;
/** Student while answers or choices are accepted (elev-pollintervall.ts → spelFasAktiv): base + random spread. */
export const SPEL_ELEV_AKTIV_MS = 1_400;
export const SPEL_ELEV_AKTIV_SPRIDNING_MS = 200;
/** SPEL_ELEV_AKTIV_MS + SPEL_ELEV_AKTIV_SPRIDNING_MS. */
export const SPEL_ELEV_AKTIV_MAX_MS = 1_600;

// ── Genomgang state poll (useGenomgangState) ──────────────────────────────────────────────
export const GENOMGANG_LARARE_MS = 2_000;
/** Student: base interval; the actual wait is GENOMGANG_ELEV_MIN_MS + random * GENOMGANG_ELEV_SPRIDNING_MS. */
export const GENOMGANG_ELEV_MIN_MS = 2_300;
export const GENOMGANG_ELEV_SPRIDNING_MS = 200;
/** GENOMGANG_ELEV_MIN_MS + GENOMGANG_ELEV_SPRIDNING_MS: the longest student wait. */
export const GENOMGANG_ELEV_MAX_MS = 2_500;
/** Student with a hidden tab. */
export const GENOMGANG_ELEV_DOLD_MS = 10_000;
/** Student while the question is open for answers (elev-pollintervall.ts → genomgangFasAktiv). */
export const GENOMGANG_ELEV_AKTIV_MIN_MS = 1_400;
export const GENOMGANG_ELEV_AKTIV_SPRIDNING_MS = 200;
/** GENOMGANG_ELEV_AKTIV_MIN_MS + GENOMGANG_ELEV_AKTIV_SPRIDNING_MS. */
export const GENOMGANG_ELEV_AKTIV_MAX_MS = 1_600;
/** Student while the slides follow the teacher (elevFoljer, outside "ovning"): every slide change is something the
 *  student waits for, so 2.0 s ± 0.1 s instead of today's 2.3–2.5 s (e2e 2026-10-26: p95 3.05 s on slide changes). */
export const GENOMGANG_ELEV_FOLJ_MIN_MS = 1_900;
export const GENOMGANG_ELEV_FOLJ_SPRIDNING_MS = 200;
/** GENOMGANG_ELEV_FOLJ_MIN_MS + GENOMGANG_ELEV_FOLJ_SPRIDNING_MS. */
export const GENOMGANG_ELEV_FOLJ_MAX_MS = 2_100;
/** Server-side: how stale a genomgang puls may be (genomgang/poll-cache.ts). */
export const GENOMGANG_PULS_CACHE_MS = 500;
/** Teacher: minimum gap between summary requests (useSammanfattningar). */
export const SAMMANFATTNING_MELLANRUM_MS = 30_000;

// ── AI second opinion (ExerciseItem → /api/forsok/omrattning) ────────────────────────────
/** Client timeout of the request; must stay above the server's OMRATTNING_TIMEOUT_MS. */
export const OMRATTNING_ANROP_TIMEOUT_MS = 15_000;

// ── Film viewing ping (FilmSpelare, via film-sedd-konstanter TITTAR_PULS_MS) ──────────────
export const FILM_PULS_MS = 20_000;

// ── Teacher lesson overview (LektionsoversiktClient) and projector (ElevVyClient) ─────────
/** /api/klass/[klassId]/lektion-data. */
export const LARARE_LEKTION_DATA_MS = 10_000;
/** /api/klass/[klassId]/lektion-config from the lesson overview. */
export const LARARE_LEKTION_CONFIG_MS = 8_000;
/** Projector: lektion-config. */
export const PROJEKTOR_CONFIG_MS = 5_000;
/** Projector: lektion-delavsnitt. */
export const PROJEKTOR_DELAVSNITT_MS = 5_000;
/** Projector: platsplacering. */
export const PROJEKTOR_PLATSPLACERING_MS = 8_000;
/** Class page (app/klass/[klassId]/page.tsx): lektion-config. */
export const KLASS_SIDA_CONFIG_MS = 15_000;

// ── Prov ──────────────────────────────────────────────────────────────────────────────────
/** ProvPassClient: the teacher's pass view while the prov is started. */
export const PROVPASS_BEVAKNING_MS = 5_000;
/** ProvElevClient: how often the student checks whether slutTid has passed; sets the 0-15 s spread of the lamna-in POST. */
export const PROV_SLUTTID_KOLL_MS = 15_000;
/** ProvElevClient: waiting for the result (/api/prov-elev/[passId]/resultat). */
export const PROV_VANTA_RESULTAT_MS = 10_000;
/** Attempts before giving up (~5 min). */
export const PROV_VANTA_RESULTAT_MAX_FORSOK = 30;
/** ProvResultatClient: the result page waiting for grading to finish. */
export const PROV_RESULTATSIDA_MS = 10_000;
/** Attempts before giving up (~5 min). */
export const PROV_RESULTATSIDA_MAX_FORSOK = 30;
